import React, { useState, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faUser, faStar, faFilter } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { ElevenLabsVoice } from '../types';
import { ElevenLabsService } from '../services/elevenLabsService';
import styles from './VoiceSelector.module.css';

interface VoiceSelectorProps {
  voices: ElevenLabsVoice[];
  selectedVoice: string;
  onVoiceSelect: (voiceId: string) => void;
  onPreviewPlay?: (voiceId: string, audioUrl: string) => void;
  isPlayingPreview?: string | null;
  loading?: boolean;
  onVoicesUpdated?: () => void; // Callback to refresh voices after favorite changes
}

export const VoiceSelector: React.FC<VoiceSelectorProps> = ({
  voices,
  selectedVoice,
  onVoiceSelect,
  onPreviewPlay,
  isPlayingPreview,
  loading = false,
  onVoicesUpdated
}) => {
  const { t } = useTranslation('audioModal');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGender, setSelectedGender] = useState<string>('all');
  const [selectedAge, setSelectedAge] = useState<string>('all');
  const [selectedAccent, setSelectedAccent] = useState<string>('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState<boolean>(false);
  const [favoritesLoading, setFavoritesLoading] = useState<string | null>(null);

  // Extract unique filter options from voices
  const genderOptions = ['all', ...new Set(voices.map(v => v.labels?.gender).filter(Boolean))];
  const ageOptions = ['all', ...new Set(voices.map(v => v.labels?.age).filter(Boolean))];
  const accentOptions = ['all', ...new Set(voices.map(v => v.labels?.accent).filter(Boolean))];

  // Filter voices based on search term and selected filters
  const filteredVoices = voices.filter(voice => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = (
      voice.name.toLowerCase().includes(searchLower) ||
      voice.description?.toLowerCase().includes(searchLower) ||
      voice.labels?.gender?.toLowerCase().includes(searchLower) ||
      voice.labels?.accent?.toLowerCase().includes(searchLower)
    );

    const matchesGender = selectedGender === 'all' || voice.labels?.gender === selectedGender;
    const matchesAge = selectedAge === 'all' || voice.labels?.age === selectedAge;
    const matchesAccent = selectedAccent === 'all' || voice.labels?.accent === selectedAccent;
    const matchesFavorites = !showFavoritesOnly || voice.isFavorite;

    return matchesSearch && matchesGender && matchesAge && matchesAccent && matchesFavorites;
  });

  // Clear all filters
  const clearFilters = () => {
    setSelectedGender('all');
    setSelectedAge('all');
    setSelectedAccent('all');
    setShowFavoritesOnly(false);
  };

  // Check if any filters are active
  const hasActiveFilters = selectedGender !== 'all' || selectedAge !== 'all' || selectedAccent !== 'all' || showFavoritesOnly;

  // Get selected voice object
  const selectedVoiceObj = voices.find(v => v.id === selectedVoice);

  const handleVoiceSelect = (voice: ElevenLabsVoice) => {
    onVoiceSelect(voice.id);
  };

  const handlePreviewPlay = (voice: ElevenLabsVoice, event: React.MouseEvent) => {
    event.stopPropagation();
    if (voice.preview_url && onPreviewPlay) {
      onPreviewPlay(voice.id, voice.preview_url);
    }
  };

  const handleToggleFavorite = async (voice: ElevenLabsVoice, event: React.MouseEvent) => {
    event.stopPropagation();
    
    if (favoritesLoading === voice.id) {
      return; // Prevent multiple clicks
    }

    setFavoritesLoading(voice.id);

    try {
      if (voice.isFavorite) {
        await ElevenLabsService.removeFavoriteVoice(voice.id);
      } else {
        await ElevenLabsService.addFavoriteVoice(voice.id);
      }
      
      // Refresh voices to get updated favorite status
      if (onVoicesUpdated) {
        onVoicesUpdated();
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
      // You might want to show a notification here
    } finally {
      setFavoritesLoading(null);
    }
  };

  const getVoiceIcon = (voice: ElevenLabsVoice) => {
    const gender = voice.labels?.gender?.toLowerCase();
    return gender === 'male' ? '👨' : gender === 'female' ? '👩' : '🎭';
  };

  const getVoiceMetadata = (voice: ElevenLabsVoice) => {
    const metadata = [];
    if (voice.labels?.gender) metadata.push(voice.labels.gender);
    if (voice.labels?.age && voice.labels.age !== 'adult') metadata.push(voice.labels.age);
    if (voice.labels?.accent && voice.labels.accent !== 'american') metadata.push(voice.labels.accent);
    return metadata.join(', ');
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading voices...</p>
      </div>
    );
  }

  return (
    <div className={styles.voiceSelector}>
      <div className={styles.header}>
        <h4>{t('voiceSelector.title')}</h4>
        {selectedVoiceObj && (
          <div className={styles.selectedVoice}>
            <span className={styles.selectedIcon}>{getVoiceIcon(selectedVoiceObj)}</span>
            <span className={styles.selectedName}>{selectedVoiceObj.name}</span>
          </div>
        )}
      </div>

      {voices.length > 10 && (
        <div className={styles.searchContainer}>
          <input
            type="text"
            placeholder={t('voiceSelector.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
      )}

      {/* Filter Pills */}
      <div className={styles.filtersContainer}>
        <div className={styles.filtersRow}>
          {/* Favorites Filter */}
          <div className={styles.favoritesFilter}>
            <button
              className={`${styles.favoritesButton} ${showFavoritesOnly ? styles.active : ''}`}
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              title="Show only favorite voices"
            >
              <FontAwesomeIcon icon={faStar} />
              <span>{t('voiceSelector.favoritesOnly')}</span>
              {voices.filter(v => v.isFavorite).length > 0 && (
                <span className={styles.favoritesCount}>
                  ({voices.filter(v => v.isFavorite).length})
                </span>
              )}
            </button>
          </div>

          <div className={styles.filterDropdown}>
            <label className={styles.filterLabel}>{t('voiceSelector.genderLabel')}</label>
            <select
              className={styles.filterSelect}
              value={selectedGender}
              onChange={(e) => setSelectedGender(e.target.value)}
            >
              {genderOptions.map(gender => (
                <option key={gender} value={gender}>
                  {gender === 'all' ? t('voiceSelector.all') : gender.charAt(0).toUpperCase() + gender.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterDropdown}>
            <label className={styles.filterLabel}>{t('voiceSelector.ageLabel')}</label>
            <select
              className={styles.filterSelect}
              value={selectedAge}
              onChange={(e) => setSelectedAge(e.target.value)}
            >
              {ageOptions.map(age => (
                <option key={age} value={age}>
                  {age === 'all' ? t('voiceSelector.all') : age.charAt(0).toUpperCase() + age.slice(1)}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.filterDropdown}>
            <label className={styles.filterLabel}>{t('voiceSelector.accentLabel')}</label>
            <select
              className={styles.filterSelect}
              value={selectedAccent}
              onChange={(e) => setSelectedAccent(e.target.value)}
            >
              {accentOptions.map(accent => (
                <option key={accent} value={accent}>
                  {accent === 'all' ? t('voiceSelector.all') : accent.charAt(0).toUpperCase() + accent.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {hasActiveFilters && (
            <button className={styles.clearFiltersButton} onClick={clearFilters}>
              {t('voiceSelector.clearFilters')}
            </button>
          )}
        </div>
      </div>

      <div className={styles.voicesContainer}>
        <div className={styles.voicesGrid}>
          {filteredVoices.map(voice => (
            <div
              key={voice.id}
              className={`${styles.voiceCard} ${selectedVoice === voice.id ? styles.selected : ''}`}
              onClick={() => handleVoiceSelect(voice)}
            >
              <div className={styles.voiceInfo}>
                <div className={styles.voiceHeader}>
                  <span className={styles.voiceIcon}>{getVoiceIcon(voice)}</span>
                  <span className={styles.voiceName}>{voice.name}</span>
                  
                  {/* Favorite Star Button */}
                  <button
                    className={`${styles.favoriteButton} ${voice.isFavorite ? styles.favorited : ''}`}
                    onClick={(e) => handleToggleFavorite(voice, e)}
                    disabled={favoritesLoading === voice.id}
                    title={voice.isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                  >
                    <FontAwesomeIcon 
                      icon={faStar}
                      className={favoritesLoading === voice.id ? styles.loading : ''}
                    />
                  </button>
                </div>
                
                {voice.description && (
                  <p className={styles.voiceDescription}>{voice.description}</p>
                )}
                
                <div className={styles.voiceMetadata}>
                  {getVoiceMetadata(voice) && (
                    <span className={styles.voiceLabels}>{getVoiceMetadata(voice)}</span>
                  )}
                </div>
              </div>

              {voice.preview_url && (
                <button
                  className={styles.previewButton}
                  onClick={(e) => handlePreviewPlay(voice, e)}
                  disabled={!onPreviewPlay}
                >
                  <FontAwesomeIcon 
                    icon={isPlayingPreview === voice.id ? faPause : faPlay}
                  />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      {filteredVoices.length === 0 ? (
        <div className={styles.noVoices}>
          <p>No voices found matching your search and filters.</p>
          {hasActiveFilters && (
            <button className={styles.clearFiltersButton} onClick={clearFilters}>
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className={styles.resultsHeader}>
          <span className={styles.resultsCount}>
            {filteredVoices.length} voice{filteredVoices.length !== 1 ? 's' : ''} found
          </span>
        </div>
      )}
    </div>
  );
};
